import { Vector3 } from "three";

// ─── Camera distance ─────────────────────────────────────────────────────────
// All zoom-related values derive from these two.
export const ZOOM_FAR = 6; // default playing distance / max zoom-out
export const ZOOM_NEAR = 3; // country selection focus / max zoom-in
// Clip cards appear when camera is closer than this distance.
// Must be between ZOOM_NEAR and ZOOM_FAR for manual-zoom trigger to work.
export const ZOOM_CLIP_THRESHOLD = 3.4;
// Zooming out past this distance while a city is selected returns to globe view.
export const ZOOM_DISMISS_DISTANCE = ZOOM_CLIP_THRESHOLD;

// ─── Camera animation ────────────────────────────────────────────────────────
export const CAMERA_DURATION = 1.2; // seconds for all camera transitions
export const AUTO_ROTATE_SPEED = 0.25; // degrees/frame for auto-rotate
export const ACHIEVEMENT_AUTO_ROTATE_SPEED = 0.025; // achievements page auto-rotate speed
export const CLIP_DEBOUNCE = 0.25; // seconds before clip cards enter/exit on drag and zoom

// ─── Landing camera ──────────────────────────────────────────────────────────
export const LANDING_POS = new Vector3(0, 2, 3);
export const LANDING_LOOK_AT = new Vector3(0, 2.5, 0);
export const IDLE_RESUME_DELAY = 0.25; // seconds of inactivity before auto-rotate resumes

// ─── Countdown camera ────────────────────────────────────────────────────────
export const COUNTDOWN_POS = new Vector3(0, 2, 3);
export const COUNTDOWN_LOOK_AT = new Vector3(-2, 2.5, 0);
export const COUNTDOWN_MOBILE_POS = new Vector3(0, 1.4, 3);
export const COUNTDOWN_MOBILE_LOOK_AT = new Vector3(0, 2, 0);

// ─── Markers & lines ─────────────────────────────────────────────────────────
export const MARKER_SIZE = 0.07; // base size of pin markers
export const MARKER_SPREAD = 0.3; // min separation between close markers (multiplier of MARKER_SIZE)
export const CLIP_SPREAD = 0.6; // min separation between close clip cards (multiplier of MARKER_SIZE)
export const LINE_WIDTH = 0.003; // continent line width at rest
export const ZOOM_SCALE_MIN = 0.7; // marker scale at maximum zoom-in
export const LINE_ZOOM_SCALE_MIN = 0.35; // line width scale at maximum zoom-in
export const CURVE_SEGMENTS_HI = 48; // line curve subdivisions (normal)
export const CURVE_SEGMENTS_LO = 24; // line curve subdivisions (low-power)
export const LINE_PULSE_EXTRA = 0.002; // extra width added at pulse head
export const ARC_HEIGHT_FACTOR = 0.075; // arc lift above sphere surface
export const LINE_DURATION = 2; // seconds each pulse travels
export const LINE_PAUSE = 5.0; // seconds each line rests between pulses

// ─── Clip cards ──────────────────────────────────────────────────────────────
export const CLIP_CARD_SCALE = 1; // clip card size relative to MARKER_SIZE
export const CLIP_CARD_STACK_SCALE = 0.15; // stack offset relative to card size
export const CHIP_SCALE = 0.45; // count chip size relative to card size

// ─── Globe colours & fill ────────────────────────────────────────────────────
export const COUNTRIES_FILL_COLOR = 0xffe679;
export const COUNTRIES_HOVER_COLOR = 0xfff4c2;
export const COUNTRIES_BORDER_COLOR = 0x6b9fdb;
export const RESTING_BORDER_OPACITY = 0.5;
export const FILL_MAX_OPACITY = 0.55;
export const FILL_ELEVATION = 1;

// ─── Heat map ────────────────────────────────────────────────────────────────
export const HEATMAP_LAT_SPACING = 6; // degrees per cell on latitude axis
export const HEATMAP_LNG_SPACING = 9; // degrees per cell on longitude axis
export const HEATMAP_DECAY_RATE = 0.96; // heat value multiplied each frame
export const HEATMAP_PAINT_STRENGTH = 0.6; // heat added per hover frame

// ─── Camera position presets ─────────────────────────────────────────────────
export const PLAYING_PRESETS = {
    default: {
        pos: new Vector3(0, 0, ZOOM_FAR),
        lookAt: new Vector3(0, 0, 0),
        lockControls: false,
    },
    achievements: {
        pos: new Vector3(1, 2, 4.2),
        lookAt: new Vector3(-0.75, 0.9, 0),
        lockControls: true,
        yRotation: true,
    },
    achievementsMobile: {
        pos: new Vector3(0, 1, ZOOM_FAR),
        lookAt: new Vector3(-0.75, 0.25, 0),
        lockControls: true,
        yRotation: true,
    },
    trivia: {
        pos: new Vector3(0, 1, 4),
        lookAt: new Vector3(-1, Math.PI / 2 + 0.25, 0),
        lockControls: true,
    },
    triviaMobile: {
        pos: new Vector3(0, 1, ZOOM_FAR),
        lookAt: new Vector3(-0.5, Math.PI / 2 - 1, 0),
        lockControls: true,
    },
    triviaPlaying: {
        pos: new Vector3(0, -0.6, ZOOM_FAR),
        lookAt: new Vector3(0, -0.6, 0),
        lockControls: false,
    },
    vote: {
        pos: new Vector3(0, 0, ZOOM_FAR),
        lookAt: new Vector3(0, 0, 0),
        lockControls: false,
    },
};
